import React, { useMemo, useState } from 'react';
import { Text, Link, Flex, Icon, Spinner, useToast, TextProps } from '@chakra-ui/react';
import { FaFilePdf } from 'react-icons/fa';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { parseFeedback } from '../../../utils/feedbackUtils';

const resolveFeedbackPdfUrl = async (source: string): Promise<string> => {
  if (/^https?:\/\//i.test(source)) {
    return source;
  }
  return getDownloadURL(ref(storage, source));
};

interface BrandFeedbackBodyProps {
  feedback: string;
  /** Dedicated PDF URL/path (e.g. UGC `editRequestPdf`); preferred over PDFs in text. */
  pdfUrl?: string | null;
  fallbackText?: string;
  compact?: boolean;
  textProps?: TextProps;
}

const BrandFeedbackBody: React.FC<BrandFeedbackBodyProps> = ({
  feedback,
  pdfUrl,
  fallbackText = 'Please replace this item and resubmit.',
  compact = false,
  textProps,
}) => {
  const toast = useToast();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const parsed = useMemo(() => parseFeedback(feedback, { pdfUrl }), [feedback, pdfUrl]);

  const handleViewPdf = () => {
    if (!parsed.pdfSource) return;

    const source = parsed.pdfSource;

    // Signed URLs can open immediately while still inside the click handler.
    // noopener makes the return value unreliable, so don't treat null as failure.
    if (/^https?:\/\//i.test(source)) {
      window.open(source, '_blank', 'noopener,noreferrer');
      return;
    }

    // Storage paths need async resolution — open a tab synchronously first so
    // popup blockers still allow it, then navigate once the download URL is ready.
    // Omit noopener here so we keep a handle to the tab; clear it after navigation.
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setLoadingPdf(true);
      resolveFeedbackPdfUrl(source)
        .then((url) => {
          window.open(url, '_blank', 'noopener,noreferrer');
        })
        .catch(() => {
          toast({
            title: 'Could not open PDF',
            description: 'Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoadingPdf(false);
        });
      return;
    }

    popup.opener = null;
    setLoadingPdf(true);
    resolveFeedbackPdfUrl(source)
      .then((url) => {
        popup.location.replace(url);
      })
      .catch(() => {
        popup.close();
        toast({
          title: 'Could not open PDF',
          description: 'Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoadingPdf(false);
      });
  };

  const bodyText = parsed.displayText ?? (parsed.pdfSource ? null : fallbackText);

  return (
    <>
      {bodyText && (
        <Text
          fontSize={compact ? '13px' : '14px'}
          color="#6E1A0A"
          lineHeight={1.4}
          noOfLines={compact ? 3 : undefined}
          {...textProps}
        >
          {bodyText}
        </Text>
      )}
      {parsed.pdfSource && (
        <Flex mt={bodyText ? 1 : 0} align="center">
          <Link
            as="button"
            type="button"
            onClick={handleViewPdf}
            fontSize={compact ? '13px' : '14px'}
            fontWeight={600}
            color="#6E1A0A"
            textDecoration="underline"
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            disabled={loadingPdf}
            opacity={loadingPdf ? 0.7 : 1}
          >
            {loadingPdf ? (
              <Spinner size="xs" color="#6E1A0A" />
            ) : (
              <Icon as={FaFilePdf} boxSize={compact ? 3 : 3.5} />
            )}
            View PDF feedback
          </Link>
        </Flex>
      )}
    </>
  );
};

export default BrandFeedbackBody;
